import {
  AuthenticateResultModel,
  GetCurrentLoginInformationsOutput,
  OrganizationUnitDto,
  OrganizationUnitLevel,
  PermissionDefinitionDtoPagedResultDto,
  UserLoginInfoDto
} from "@/api/appService";
import api from '@/api'
import {ActionContext} from "vuex";

export interface UserState {
  token: string;
  tokenExpireTime?: number

  roles: string[]
  permissions?: string[]
  ou?: OrganizationUnitDto;
  userInfo?: UserLoginInfoDto
}

const cache = JSON.parse(window.localStorage.getItem('user') || '{}')
const setStore = (state:UserState) => {
  window.localStorage.setItem('user',JSON.stringify(state))
}

const defaultOrg = {
  displayName: '童萌',
  level: OrganizationUnitLevel.总部,
  detail: {
    logoImgUrl: '/logo.png'
  }
} as OrganizationUnitDto

let checkInterval:any = 0;

const initializeState = {
  ...{
    token:'',
    tokenExpireTime: undefined,
    roles:[],
    permissions:[],
    ou: defaultOrg,
    userInfo: undefined
  },
  ...cache
} as UserState

export default {
  namespaced: true,
  state: () => ({...initializeState}),
  mutations: {
    SET_TOKEN(state: UserState,  model: AuthenticateResultModel) {
      state.token = model.accessToken!
      if(model.expireInSeconds){
        state.tokenExpireTime = (new Date().getTime()) + model.expireInSeconds * 1000
      } else {
        state.tokenExpireTime = (new Date().getTime()) * 1000 * 3600 * 24 * 30
      }
      setStore(state)
    },
    SET_ROLES(state: UserState, roles: string[] | string | undefined) {
      if (roles) {
        if (Array.isArray(roles)) {
          state.roles = roles;
        } else {
          state.roles = roles.split(',')
        }
      } else {
        state.roles = []
      }
      setStore( state)

    },
    SET_PERMISSIONS(state: UserState, permissions: string[] | undefined) {
      state.permissions = permissions ?? []
      setStore( state)
    },
    SET_USER(state: UserState, user: UserLoginInfoDto | undefined) {
      state.userInfo = user;
      setStore( state)
    },
    SET_OU(state: UserState, ou: OrganizationUnitDto | undefined) {
      state.ou = ou ?? defaultOrg;
      setStore(state)
    },
    LOGOUT(state: UserState) {
      state.userInfo = undefined
      state.token = ''
      state.roles = []
      state.permissions = []
      state.ou = undefined
      if(checkInterval){
        clearInterval(checkInterval)
        checkInterval = undefined
      }

      window.localStorage.removeItem('user')
      window.localStorage.removeItem('permission')
    },


  },
  actions: {

    checkToken(context:ActionContext<UserState, any>):Promise<void>{
      return new Promise<void>((resolve,reject)=>{
        checkInterval = setInterval(()=>{
          if(!context.state.token){
            context.commit('LOGOUT');
            reject()
            return
          }
          if(!context.state.tokenExpireTime){
            context.commit('LOGOUT')
            reject()
            return
          }
          const now = new Date();
          const timeSpan = (context.state.tokenExpireTime! - now.getTime())/ 1000
          if(timeSpan <= 0){
            context.commit('LOGOUT')
            reject();
          } else if(timeSpan < 60){
            context.dispatch('refreshToken').then(()=>{
              console.log('refresh token success')
            }).catch(()=>{
              context.commit('LOGOUT')
              reject()
            })
          }

        },5000)
      })

    },
    refreshToken(context: ActionContext<UserState, any>):Promise<void>{
      return new Promise<void>((resolve,reject)=>{
        api.tokenAuth.refreshToken().then(res=>{
          context.commit('SET_TOKEN',res)
          resolve()
        }).catch(()=>{
          reject()
        })
      })
    },

    getUserInfo(context: ActionContext<UserState, any>): Promise<any> {
      return new Promise<any>((resolve) => {

        const fetchPermissionDefinitions = getPermissionDefinitions();
        const fetchCurrentLoginInfo = api.session.getCurrentLoginInformations();
        console.log('user/getUserInfo beginFetch')

        Promise.all([fetchPermissionDefinitions, fetchCurrentLoginInfo]).then(([r1, r2]) => {
          const permissionResponse = r1 as PermissionDefinitionDtoPagedResultDto
          const currentLoginInfo = r2 as GetCurrentLoginInformationsOutput;

          console.log('user/getUserInfo fetch success:', permissionResponse,currentLoginInfo)

          context.commit('SET_USER', currentLoginInfo.user)
          context.commit('SET_ROLES',currentLoginInfo.user?.roles)

          context.commit('SET_PERMISSIONS', currentLoginInfo.permissions);
          context.commit('SET_OU', currentLoginInfo.organizationUnit);

          console.log('user/getUserInfo set completed', context.state)


          let level;
          if (currentLoginInfo.organizationUnit) {
            level = currentLoginInfo.organizationUnit.level
          } else {
            if (context.getters['isAdmin'] === true || context.getters['isHeadquarter'] === true) {
              level = OrganizationUnitLevel.总部
            }
          }
          console.log('user/getUserInfo begin generateMenus')
          context.dispatch('permission/generateMenus', {
            permissions: currentLoginInfo.permissions,
            roles: [],
            level: level,
            permissionDefinitions: permissionResponse.items!
          },{root:true}).then(() => {
            console.log('user/getUserInfo generate menus completed')
            resolve();
          })


        })

        getPermissionDefinitions().then(permissionResponse => {
          api.session.getCurrentLoginInformations().then()
        })
      })
    },
    changeOu(context: ActionContext<UserState, any>, ou: OrganizationUnitDto | undefined): Promise<any> {
      return new Promise<any>((resolve) => {
        context.commit('SET_OU', ou)
        getPermissionDefinitions().then(permissionResponse => {
          context.dispatch('permission/generateMenus', {
            permissions: context.state.permissions,
            roles: context.state.roles,
            level: ou?.level ?? OrganizationUnitLevel.总部,
            permissionDefinitions: permissionResponse.items!
          },{root:true})
        })
      })

    }
  },

  getters: {
    ouId(state: UserState): number | undefined {
      return state.ou?.id;
    },
    ou(state: UserState): OrganizationUnitDto | undefined {
      return state.ou
    },
    token(state: UserState): string | undefined {
      return state.token
    },
    roles(state: UserState): string[] | undefined {
      return state.roles;
    },
    isAdmin(state: UserState): boolean {
      return state.roles.some(x => x === 'Admin' || x === 'admin');
    },
    isHeadquarter(state: UserState): boolean {
      return state.roles.some(x => x === 'Headquarter')
    },
    avatar(state: UserState): string {
      return state.userInfo?.headImgUrl ?? ''
    },
    name(state: UserState): string {
      return state.userInfo?.surname ?? state.userInfo?.name ?? ''
    },
    permissions(state: UserState): string[] {
      return state.permissions ?? []
    }
  }
}


function getPermissionDefinitions() {
  return api.permissionDefinition.getAll({sorting: 'displayOrder asc,id asc', maxResultCount: 65536});
}
